
import { KolDbEntry, KolTrackerEntry } from '../ImportDataDialogV2';

export interface MatchResult {
  kolDbId: string | null;
  matchedCreator: string | null;
  confidence: number;
  matchReason: string;
  warnings: string[];
  tier?: string;
  language?: string;
  channel?: string;
}

export interface MatchingContext {
  kolDbData: KolDbEntry[];
  kolTrackerData: KolTrackerEntry[];
}

// String similarity function using Levenshtein distance
function calculateSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1;
  
  const maxLength = Math.max(s1.length, s2.length);
  if (maxLength === 0) return 1;
  
  const distance = levenshteinDistance(s1, s2);
  return 1 - distance / maxLength;
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }
  
  return matrix[str2.length][str1.length];
}

// Extract Telegram handler from URL
export function extractTelegramHandler(url: string): string | null {
  if (!url) return null;
  const match = url.match(/t\.me\/([^\/]+)/i);
  return match ? match[1] : null;
}

// Intelligent matching function with confidence scoring and additional field extraction
export function findIntelligentMatch(
  kolName: string, 
  postLink: string, 
  context: MatchingContext
): MatchResult {
  const { kolDbData, kolTrackerData } = context;
  
  // Level 1: Exact handler match with frequency analysis
  if (postLink && postLink.includes('t.me/')) {
    const extractedHandler = extractTelegramHandler(postLink);
    if (extractedHandler) {
      const handlerMatches = kolTrackerData.filter(tracker => {
        if (tracker.post_link && tracker.post_link.includes('t.me/')) {
          const trackerHandler = extractTelegramHandler(tracker.post_link);
          return trackerHandler && trackerHandler.toLowerCase() === extractedHandler.toLowerCase();
        }
        return false;
      });
      
      if (handlerMatches.length > 0) {
        // Count frequency of each kol_db_id and collect additional fields
        const kolDbIdCounts: Record<string, { 
          count: number; 
          latestDate: Date | null;
          tier?: string;
          language?: string;
          channel?: string;
        }> = {};
        
        handlerMatches.forEach(match => {
          if (match.kol_db_id) {
            if (!kolDbIdCounts[match.kol_db_id]) {
              kolDbIdCounts[match.kol_db_id] = { 
                count: 0, 
                latestDate: null,
                tier: match.tier,
                language: match.language,
                channel: match.channel
              };
            }
            kolDbIdCounts[match.kol_db_id].count++;
            
            // Track latest date for recency weighting
            const matchDate = new Date(); // In real scenario, use tracker creation date
            if (!kolDbIdCounts[match.kol_db_id].latestDate || matchDate > kolDbIdCounts[match.kol_db_id].latestDate) {
              kolDbIdCounts[match.kol_db_id].latestDate = matchDate;
              // Update additional fields from the most recent match
              kolDbIdCounts[match.kol_db_id].tier = match.tier;
              kolDbIdCounts[match.kol_db_id].language = match.language;
              kolDbIdCounts[match.kol_db_id].channel = match.channel;
            }
          }
        });
        
        // Find the most frequent kol_db_id
        const mostFrequentId = Object.entries(kolDbIdCounts)
          .sort((a, b) => {
            // Primary sort: frequency
            if (b[1].count !== a[1].count) {
              return b[1].count - a[1].count;
            }
            // Secondary sort: recency
            const aTime = a[1].latestDate?.getTime() || 0;
            const bTime = b[1].latestDate?.getTime() || 0;
            return bTime - aTime;
          })[0]?.[0];
        
        if (mostFrequentId) {
          const kolDbEntry = kolDbData.find(kol => kol.id === mostFrequentId);
          const frequency = kolDbIdCounts[mostFrequentId].count;
          const totalMatches = handlerMatches.length;
          const frequencyRatio = frequency / totalMatches;
          const additionalFields = kolDbIdCounts[mostFrequentId];
          
          // Calculate confidence based on frequency and consistency
          let confidence = 70 + (frequencyRatio * 20); // Base 70% + up to 20% for frequency
          
          const warnings: string[] = [];
          
          // Check name similarity if we have both names
          if (kolName && kolDbEntry?.creator_name) {
            const nameSimilarity = calculateSimilarity(kolName, kolDbEntry.creator_name);
            if (nameSimilarity > 0.8) {
              confidence += 10; // Boost for name similarity
            } else if (nameSimilarity < 0.4) {
              confidence -= 15; // Penalty for name mismatch
              warnings.push(`Name mismatch: "${kolName}" vs "${kolDbEntry.creator_name}"`);
            }
          }
          
          if (frequencyRatio < 0.7) {
            warnings.push(`Low frequency match: ${frequency}/${totalMatches} occurrences`);
          }
          
          return {
            kolDbId: mostFrequentId,
            matchedCreator: kolDbEntry ? `${kolDbEntry.creator_name} (${kolDbEntry.social_media_handler || 'No handler'})` : 'Unknown Creator',
            confidence: Math.min(100, Math.max(0, confidence)),
            matchReason: `Handler match: ${extractedHandler} (${frequency}/${totalMatches} occurrences)`,
            warnings,
            tier: additionalFields.tier,
            language: additionalFields.language,
            channel: additionalFields.channel
          };
        }
      }
    }
  }
  
  // Level 2: Name-based matching with fuzzy logic
  if (kolName) {
    const nameMatches = kolTrackerData.filter(tracker => {
      if (!tracker.name) return false;
      const similarity = calculateSimilarity(kolName, tracker.name);
      return similarity > 0.7; // 70% similarity threshold
    });
    
    if (nameMatches.length > 0) {
      // Find best name match
      const bestMatch = nameMatches
        .map(match => ({
          ...match,
          similarity: calculateSimilarity(kolName, match.name || '')
        }))
        .sort((a, b) => b.similarity - a.similarity)[0];
      
      if (bestMatch.kol_db_id) {
        const kolDbEntry = kolDbData.find(kol => kol.id === bestMatch.kol_db_id);
        const confidence = 40 + (bestMatch.similarity * 40); // Base 40% + similarity bonus
        
        const warnings: string[] = [];
        if (bestMatch.similarity < 0.9) {
          warnings.push(`Fuzzy name match: "${kolName}" ≈ "${bestMatch.name}"`);
        }
        
        return {
          kolDbId: bestMatch.kol_db_id,
          matchedCreator: kolDbEntry ? `${kolDbEntry.creator_name} (${kolDbEntry.social_media_handler || 'No handler'})` : 'Unknown Creator',
          confidence: Math.min(100, confidence),
          matchReason: `Name similarity: ${(bestMatch.similarity * 100).toFixed(1)}%`,
          warnings,
          tier: bestMatch.tier,
          language: bestMatch.language,
          channel: bestMatch.channel
        };
      }
    }
  }
  
  // No match found
  return {
    kolDbId: null,
    matchedCreator: 'No match found',
    confidence: 0,
    matchReason: 'No matching records found',
    warnings: ['No reliable match could be established']
  };
}

// Confidence level classification
export function getConfidenceLevel(confidence: number): {
  level: 'very-high' | 'high' | 'medium' | 'low' | 'very-low';
  label: string;
  color: string;
} {
  if (confidence >= 90) {
    return { level: 'very-high', label: 'Very High', color: 'green' };
  } else if (confidence >= 70) {
    return { level: 'high', label: 'High', color: 'blue' };
  } else if (confidence >= 50) {
    return { level: 'medium', label: 'Medium', color: 'yellow' };
  } else if (confidence >= 30) {
    return { level: 'low', label: 'Low', color: 'orange' };
  } else {
    return { level: 'very-low', label: 'Very Low', color: 'red' };
  }
}
